import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, LogOut, RefreshCw, Settings2 } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { create } from "zustand";
import {
  copilotGhostText,
  lspPosition,
  type CopilotQuota,
  type CopilotQuotaBucket,
  type CopilotSignIn,
  type CopilotStatus,
  type LanguageId,
} from "@ide/shared";
import { CopilotMark, GithubMark } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/controls";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/overlay";
import { Spinner } from "@/components/ui/primitives";
import { desktop } from "@/desktop/bridge";
import type { Completion } from "@/editor/ai-complete";
import {
  breakdownRows,
  formatCount,
  totalTokens,
  useAiUsage,
  usageRows,
  type ProviderUsage,
} from "@/lib/ai-usage";
import { cn } from "@/lib/utils";
import {
  formatDay,
  formatMoment,
  tokensLabel,
  toneOfUsed,
  toneText,
  UsageAction,
  UsageCard,
  UsageHover,
  UsageLine,
  UsageNotice,
  UsageRows,
  UsageSection,
  UsageStat,
} from "@/workspace/ai-usage";
import { openChat } from "@/desktop/chat";
import { activeWorkspace } from "@/workspace/file-ops";
import type { PluginCommand } from "./registry";

/**
 * GitHub Copilot oficial (plugin `kompil.github-copilot`). No app desktop, o servidor de
 * linguagem do Copilot roda no processo principal (apps/desktop/src/copilot.ts); aqui ficam o
 * estado, o login por código de dispositivo e o provedor do texto fantasma do editor.
 */
const INLINE_KEY = "kompil.copilot.inline";
function readInline() {
  try {
    return localStorage.getItem(INLINE_KEY) !== "0";
  } catch {
    return true;
  }
}

export const useCopilot = create<{
  /** Plugin instalado e ligado (a barra de status monta enquanto ele está ativo). */
  on: boolean;
  status: CopilotStatus | null;
  signIn: CopilotSignIn | null;
  dialog: boolean;
  /** Sugestões embutidas (texto fantasma) ligadas. */
  inline: boolean;
}>(() => ({ on: false, status: null, signIn: null, dialog: false, inline: readInline() }));

export function setInlineSuggestions(on: boolean) {
  useCopilot.setState({ inline: on });
  try {
    localStorage.setItem(INLINE_KEY, on ? "1" : "0");
  } catch {
    // sem armazenamento: vale só nesta sessão
  }
}

/** Pronto para sugerir: plugin ligado, sugestões embutidas ligadas, app desktop e conta conectada. */
export const copilotReady = () => {
  const s = useCopilot.getState();
  return s.on && s.inline && desktop !== null && s.status?.state === "ready";
};

/** Ids de linguagem do VS Code, que o Copilot usa para escolher o contexto. */
const COPILOT_LANGUAGE: Partial<Record<LanguageId, string>> = {
  tsx: "typescriptreact",
  jsx: "javascriptreact",
  shell: "shellscript",
};

/** Sugestão do Copilot para o cursor, no formato do texto fantasma do editor. */
export async function copilotCompletion(
  input: { text: string; offset: number; tabSize: number },
  file: { projectId: string; path: string; language: LanguageId },
): Promise<Completion> {
  if (!desktop || !copilotReady()) return "";
  const position = lspPosition(input.text, input.offset);
  const items = await desktop.copilot.complete({
    uri: `file:///kompil/${file.projectId}/${file.path.split("/").map(encodeURIComponent).join("/")}`,
    languageId: COPILOT_LANGUAGE[file.language] ?? file.language,
    text: input.text,
    line: position.line,
    character: position.character,
    tabSize: input.tabSize,
    insertSpaces: true,
  });
  for (const item of items) {
    const text = copilotGhostText(input.text, input.offset, item);
    if (text) {
      return {
        text,
        onShown: () => desktop?.copilot.shown(item),
        onAccept: () => {
          desktop?.copilot.accepted(item);
          useAiUsage
            .getState()
            .record(
              "copilot",
              file.projectId.toLowerCase(),
              { accepted: 1 },
              { feature: "Sugestões no editor" },
            );
        },
      };
    }
  }
  return "";
}

export async function startSignIn() {
  if (!desktop) {
    toast("O GitHub Copilot funciona no Kompil Desktop.");
    return;
  }
  useCopilot.setState({ dialog: true, signIn: null });
  const result = await desktop.copilot.signIn();
  if (!result.ok) {
    useCopilot.setState({ dialog: false });
    toast.error(result.error ?? "Não foi possível entrar no GitHub Copilot.");
  } else if (result.alreadySignedIn) {
    useCopilot.setState({ dialog: false });
    toast.success("GitHub Copilot conectado");
  } else useCopilot.setState({ signIn: result });
}

async function signOut() {
  await desktop?.copilot.signOut();
  toast("GitHub Copilot desconectado");
}

export const COPILOT_GITHUB_COMMANDS: PluginCommand[] = [
  { id: "github-copilot.sign-in", title: "GitHub Copilot: conectar conta", run: () => void startSignIn() },
  { id: "github-copilot.sign-out", title: "GitHub Copilot: desconectar", run: () => void signOut() },
];

const LABEL: Record<CopilotStatus["state"], string> = {
  starting: "Copilot: iniciando",
  "signed-out": "Copilot: conectar",
  ready: "Copilot",
  unavailable: "Copilot indisponível",
  error: "Copilot: erro",
};

const PLAN_LABEL: Record<string, string> = {
  free: "Copilot Free",
  individual: "Copilot Pro",
  individual_pro: "Copilot Pro+",
  business: "Copilot Business",
  enterprise: "Copilot Enterprise",
};

/** A cota que manda: no plano gratuito, a menor entre chat e sugestões; nos pagos, os pedidos premium. */
export function mainQuota(q: CopilotQuota | null | undefined): CopilotQuotaBucket | null {
  if (!q) return null;
  if (q.plan === "free") {
    const limited = [q.chat, q.completions].filter((b): b is CopilotQuotaBucket =>
      Boolean(b && !b.unlimited),
    );
    return limited.sort((x, y) => x.percentRemaining - y.percentRemaining)[0] ?? q.chat ?? q.completions;
  }
  return q.premium ?? q.chat;
}

const hasQuotaApi = () => typeof desktop?.copilot.quota === "function";

/**
 * Cota da conta, lida pelo servidor do Copilot no GitHub: ao abrir, a cada 5 minutos e sempre que o
 * servidor avisa que mudou (depois de uma sugestão ou de uma resposta do chat).
 */
export function useCopilotQuota() {
  const ready = useCopilot((s) => s.status?.state === "ready");
  const qc = useQueryClient();
  useEffect(() => {
    if (!ready || !desktop || typeof desktop.copilot.onQuota !== "function") return;
    return desktop.copilot.onQuota((q) => qc.setQueryData(["copilot", "quota"], q));
  }, [ready, qc]);
  return useQuery({
    queryKey: ["copilot", "quota"],
    queryFn: () => desktop!.copilot.quota(),
    enabled: ready && hasQuotaApi(),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** Quanto da cota já foi usado (0–100+); ilimitado: null. */
const usedPercent = (b: CopilotQuotaBucket | null | undefined) =>
  !b || b.unlimited ? null : Math.max(0, 100 - b.percentRemaining);

/** Texto curto da barra de status: "Cota atingida", "27% usado" ou só "Copilot" (ilimitado). */
export function quotaLabel(q: CopilotQuota | null | undefined): { text: string; used: number | null } {
  const used = usedPercent(mainQuota(q));
  if (used === null) return { text: "Copilot", used: null };
  if (used >= 100) return { text: "Cota atingida", used: 100 };
  return { text: `${Math.floor(used)}% usado`, used };
}

const BUDGETS_URL = "https://github.com/settings/billing/budgets";
const COPILOT_SETTINGS_URL = "https://github.com/settings/copilot";
const PLANS_URL = "https://github.com/features/copilot/plans";

/**
 * Barra de status (canto direito): a cota da conta do Copilot. Passar o mouse abre o cartão com os
 * créditos, o orçamento adicional e o uso por modelo e por projeto; sem conta, o convite para entrar.
 */
export function CopilotStatusItem() {
  const status = useCopilot((s) => s.status);
  useEffect(() => {
    useCopilot.setState({ on: true });
    if (!desktop) {
      useCopilot.setState({ status: { state: "unavailable", message: "Só no app desktop." } });
      return () => useCopilot.setState({ on: false });
    }
    const off = desktop.copilot.onStatus((s) => useCopilot.setState({ status: s }));
    void desktop.copilot.status().then((s) => s && useCopilot.setState({ status: s }));
    return () => {
      off();
      useCopilot.setState({ on: false });
    };
  }, []);
  const quota = useCopilotQuota();
  const state = status?.state ?? "starting";
  const ready = state === "ready";
  const label = ready ? quotaLabel(quota.data) : { text: LABEL[state], used: null };
  return (
    <UsageHover
      label={`GitHub Copilot: ${label.text}`}
      className={toneText[toneOfUsed(label.used)]}
      trigger={
        <>
          <CopilotMark className={cn("h-3.5 w-3.5", !ready && "opacity-50")} />
          {label.text}
        </>
      }
    >
      {ready ? (
        <CopilotUsageCard
          user={status?.user}
          quota={quota.data ?? null}
          loading={quota.isLoading}
          refresh={() => void quota.refetch()}
        />
      ) : (
        <CopilotSignInCard state={state} message={status?.message} />
      )}
    </UsageHover>
  );
}

/** Sem conta (ou o servidor subindo): o convite para entrar e autorizar o Kompil no GitHub. */
function CopilotSignInCard({ state, message }: { state: CopilotStatus["state"]; message?: string }) {
  return (
    <UsageCard
      icon={<CopilotMark className="h-[18px] w-[18px]" />}
      title="GitHub Copilot"
      subtitle={state === "starting" ? "Iniciando o servidor do Copilot…" : "Conta não conectada"}
    >
      {state === "starting" ? (
        <div className="flex items-center gap-2 text-[12.5px] text-fg-muted">
          <Spinner className="h-3.5 w-3.5" /> Preparando o Copilot neste computador…
        </div>
      ) : state === "signed-out" ? (
        <>
          <p className="text-[12.5px] leading-relaxed text-fg-muted">
            Entre com a sua conta do GitHub para usar o Copilot e ver aqui os créditos, o orçamento e o uso
            por modelo e por projeto.
          </p>
          <ol className="space-y-1.5 text-[12px] leading-snug text-fg-muted">
            <li>1. O Kompil mostra um código e abre o GitHub no navegador.</li>
            <li>2. Cole o código e autorize o GitHub Copilot a ser usado no Kompil.</li>
            <li>3. Volte para cá: a conta conecta sozinha.</li>
          </ol>
          {message ? <UsageNotice tone="warn">{message}</UsageNotice> : null}
          <UsageAction
            variant="primary"
            icon={<GithubMark className="h-3.5 w-3.5" />}
            onClick={() => void startSignIn()}
          >
            Entrar com o GitHub
          </UsageAction>
        </>
      ) : (
        <UsageNotice tone="warn">{message ?? "O Copilot não está disponível agora."}</UsageNotice>
      )}
    </UsageCard>
  );
}

function CopilotUsageCard({
  user,
  quota,
  loading,
  refresh,
}: {
  user?: string;
  quota: CopilotQuota | null;
  loading: boolean;
  refresh: () => void;
}) {
  const months = useAiUsage((s) => s.months);
  const breakdown = useAiUsage((s) => s.breakdown);
  const inline = useCopilot((s) => s.inline);
  const current = activeWorkspace.current?.detail.project.id.toLowerCase() ?? null;
  const projects = usageRows(months, ["copilot"]);
  const models = breakdownRows(breakdown, "copilot", "model");
  const features = breakdownRows(breakdown, "copilot", "feature");
  const plan = quota?.plan ? (PLAN_LABEL[quota.plan] ?? `Copilot ${quota.plan}`) : "GitHub Copilot";
  const main = mainQuota(quota);
  const credits = quota?.plan === "free" ? null : quota?.premium;
  const exhausted = (usedPercent(main) ?? 0) >= 100;
  const overage = credits?.overageLimit ? ((credits.overageUsed ?? 0) / credits.overageLimit) * 100 : null;
  const renews = quota?.resetAt
    ? `Renova ${formatMoment(quota.resetAt)}`
    : quota?.resetDate
      ? `Renova ${formatDay(quota.resetDate)}`
      : undefined;
  const describeRequests = (u: ProviderUsage) =>
    [
      u.requests ? `${formatCount(u.requests)} ${u.requests === 1 ? "pedido" : "pedidos"}` : null,
      u.premium ? `~${formatCount(u.premium)} premium` : null,
      u.accepted ? `${formatCount(u.accepted)} aceitas` : null,
      totalTokens(u) ? tokensLabel(totalTokens(u)) : null,
    ]
      .filter(Boolean)
      .join(" · ");
  return (
    <UsageCard
      icon={<CopilotMark className="h-[18px] w-[18px]" />}
      title={plan}
      subtitle={user ? `@${user}` : undefined}
      action={
        <UsageAction variant="outline" onClick={() => desktop?.openExternal(BUDGETS_URL)}>
          Gerenciar orçamento
        </UsageAction>
      }
      footer={
        <>
          <UsageAction icon={<RefreshCw className="h-3.5 w-3.5" />} onClick={refresh}>
            Atualizar
          </UsageAction>
          <UsageAction
            icon={<ExternalLink className="h-3.5 w-3.5" />}
            onClick={() => desktop?.openExternal(COPILOT_SETTINGS_URL)}
          >
            No GitHub
          </UsageAction>
          <span className="ml-auto" />
          <UsageAction
            icon={<Settings2 className="h-3.5 w-3.5" />}
            onClick={() => useCopilot.setState({ dialog: true })}
          >
            Conta
          </UsageAction>
        </>
      }
    >
      {loading ? (
        <div className="flex items-center gap-2 text-[12px] text-fg-subtle">
          <Spinner className="h-3.5 w-3.5" /> Lendo a cota no GitHub…
        </div>
      ) : !quota ? (
        <UsageNotice>
          {hasQuotaApi()
            ? "O GitHub não informou a cota agora. Tente atualizar."
            : "Atualize o Kompil Desktop para ver a cota da conta."}
        </UsageNotice>
      ) : (
        <>
          {exhausted && credits?.overagePermitted ? (
            <UsageNotice>
              O orçamento adicional está configurado. O uso continua até a cota ser renovada.
            </UsageNotice>
          ) : exhausted ? (
            <UsageNotice tone="warn">
              A cota do mês acabou. Os modelos incluídos no plano (0x) continuam liberados até a renovação.
              <span className="mt-2 flex flex-wrap gap-1.5">
                <UsageAction variant="primary" onClick={() => openChat("copilot")}>
                  Usar um modelo incluído
                </UsageAction>
                <UsageAction variant="outline" onClick={() => desktop?.openExternal(PLANS_URL)}>
                  Ver o plano
                </UsageAction>
              </span>
            </UsageNotice>
          ) : null}
          {credits ? (
            <UsageStat
              label="Créditos"
              percentUsed={usedPercent(credits)}
              value={
                credits.unlimited ? <span className="text-[16px] font-semibold">Ilimitado</span> : undefined
              }
              aside={renews}
              hint={
                credits.entitlement !== undefined && credits.used !== undefined
                  ? `${formatCount(credits.used)} de ${formatCount(credits.entitlement)} pedidos premium incluídos no mês`
                  : undefined
              }
            />
          ) : null}
          {overage !== null && credits?.overageLimit ? (
            <UsageStat
              label="Orçamento adicional"
              percentUsed={overage}
              aside={renews}
              hint={`${formatCount(credits.overageUsed ?? 0)} de ${formatCount(credits.overageLimit)} pedidos extras (cobrados à parte)`}
            />
          ) : null}
          {(
            [
              ["Chat", quota.chat],
              ["Sugestões de código", quota.completions],
            ] as Array<[string, CopilotQuotaBucket | null]>
          ).map(([name, b]) =>
            b ? (
              b.unlimited ? (
                <UsageLine key={name} label={name}>
                  Ilimitado
                </UsageLine>
              ) : (
                <UsageStat
                  key={name}
                  label={name}
                  percentUsed={usedPercent(b)}
                  aside={credits ? undefined : renews}
                />
              )
            ) : null,
          )}
          <UsageLine label="Sugestões embutidas">
            <span className="text-fg-muted">{inline ? "Habilitado" : "Desabilitado"}</span>
            <Switch
              checked={inline}
              onCheckedChange={setInlineSuggestions}
              aria-label="Sugestões embutidas do Copilot"
            />
          </UsageLine>
        </>
      )}
      <UsageSection title="Este mês, por modelo">
        <UsageRows
          rows={models.map((m) => ({ key: m.name, name: m.name, usage: m.usage }))}
          weight={(u) => u.premium + u.requests}
          describe={describeRequests}
          empty="Nenhum pedido ao Copilot Chat neste mês."
        />
      </UsageSection>
      <UsageSection title="Por projeto">
        <UsageRows
          rows={projects.map((r) => ({ key: r.projectId, name: r.name, usage: r.usage }))}
          current={current}
          weight={(u) => u.premium + u.requests + u.accepted / 10}
          describe={describeRequests}
        />
      </UsageSection>
      {features.length ? (
        <UsageSection title="Por recurso">
          {features.map((f) => (
            <UsageLine key={f.name} label={f.name}>
              <span className="tabular-nums text-fg-muted">{describeRequests(f.usage)}</span>
            </UsageLine>
          ))}
        </UsageSection>
      ) : null}
      <p className="text-[11px] leading-snug text-fg-subtle">
        A cota vem do GitHub. Modelos, projetos e recursos são contados neste computador (o premium é estimado
        pelo multiplicador de cada modelo).
      </p>
    </UsageCard>
  );
}

/** Diálogo do login (código de dispositivo) e da conta conectada. */
export function CopilotOverlay() {
  const { dialog, signIn, status } = useCopilot();
  const ready = status?.state === "ready";
  useEffect(() => {
    if (ready && signIn) {
      useCopilot.setState({ dialog: false, signIn: null });
      toast.success(`GitHub Copilot conectado${status?.user ? ` como @${status.user}` : ""}`);
    }
  }, [ready, signIn, status?.user]);
  const close = () => useCopilot.setState({ dialog: false, signIn: null });
  return (
    <Dialog open={dialog} onOpenChange={(v) => (v ? null : close())}>
      <DialogContent
        title="GitHub Copilot"
        description={
          ready
            ? "Sugestões do Copilot aparecem como texto fantasma enquanto você digita. Tab aceita, Esc descarta."
            : "Conecte sua conta do GitHub com Copilot (o plano gratuito serve)."
        }
      >
        {ready ? (
          <div className="flex items-center gap-3 rounded-lg border border-border bg-bg-subtle p-3 text-[13px]">
            <Check className="h-4 w-4" /> Conectado{status?.user ? ` como @${status.user}` : ""}
          </div>
        ) : signIn?.userCode ? (
          <div className="space-y-4">
            <p className="text-[13px] text-fg-muted">
              1. Copie o código. 2. Cole na página do GitHub que abriu no navegador e autorize o GitHub
              Copilot a ser usado no Kompil. 3. Volte aqui — conecta sozinho.
            </p>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border-strong bg-bg-subtle px-4 py-3">
              <span className="font-mono text-2xl font-semibold tracking-[0.2em]">{signIn.userCode}</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(signIn.userCode ?? "");
                  toast("Código copiado");
                }}
              >
                <Copy className="h-3.5 w-3.5" /> Copiar
              </Button>
            </div>
            <p className="flex items-center gap-2 text-[12px] text-fg-subtle">
              <Spinner className="h-3 w-3" /> Esperando a autorização no GitHub…
            </p>
          </div>
        ) : (
          <p className="flex items-center gap-2 text-[13px] text-fg-muted">
            <Spinner className="h-3.5 w-3.5" /> Preparando o login…
          </p>
        )}
        <DialogFooter>
          {ready ? (
            <Button
              variant="ghost"
              onClick={() => {
                close();
                void signOut();
              }}
            >
              <LogOut className="h-3.5 w-3.5" /> Desconectar
            </Button>
          ) : signIn?.verificationUri ? (
            <Button variant="secondary" onClick={() => desktop?.openExternal(signIn.verificationUri!)}>
              <ExternalLink className="h-3.5 w-3.5" /> Abrir o GitHub
            </Button>
          ) : null}
          <Button variant="primary" onClick={close}>
            {ready ? "Pronto" : "Fechar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
